import React, { useState } from 'react'

import {
  Edit as EditIcon,
  Home as HomeIcon,
  Label as LabelIcon,
  Print as PrintIcon,
} from '@material-ui/icons'

import { useAsyncPrint } from 'src/components/ToPrint/useAsyncPrint'
import { ShipmentPalletLabels } from 'src/modules/supply_chain/components/freight/ShipmentPalletLabels'
import { ShipmentPlacard } from 'src/modules/supply_chain/components/freight/ShipmentPlacard'
import { PackingSlip } from 'src/modules/supply_chain/components/freight/PackingSlip'
import { FreightShipmentDialog } from 'src/modules/supply_chain/components/freight/ShipmentDialog'
import {
  AddressField,
  DateTimeField,
  LinkedPathField,
  StatusField,
  RelatedLinkedField,
  ValueField,
} from 'src/components/Table'
import { DetailComponent } from './base'

interface ObjInfo {
  data: any
  isLoading: boolean
}

interface DetailTableProps {
  obj: ObjInfo
  refresh?: () => void
}

export const FreightShipmentDetailTable = (props: DetailTableProps) => {
  const { obj, refresh } = props
  const [updateOpen, setUpdateOpen] = useState(false)
  const { print: asyncPrint, renderPrintComponent: renderAsyncPrintComponent } = useAsyncPrint()

  return (
    <>
      {renderAsyncPrintComponent()}
      {updateOpen && (
        <FreightShipmentDialog
          open={updateOpen}
          shipment={obj.data}
          update
          handleClose={() => {
            setUpdateOpen(false)
          }}
          onSave={refresh}
        />
      )}
      <DetailComponent
        obj={obj}
        Icon={HomeIcon}
        menuItems={[
          {
            Icon: EditIcon,
            actions: [{ title: 'Edit Freight Shipment', onClick: () => setUpdateOpen(true) }],
          },
          {
            Icon: PrintIcon,
            actions: [
              {
                title: 'Print Shipment Placard',
                onClick: () => asyncPrint((props) => <ShipmentPlacard {...props} shipment={obj.data} />),
              },
              {
                title: 'Print Shipment Pallet Placards',
                onClick: () => asyncPrint((props) => <ShipmentPalletLabels {...props} shipment={obj.data} />),
              },
              {
                title: 'Print Packing Slip',
                onClick: () => asyncPrint((props) => <PackingSlip {...props} guid={obj.data.guid || ''} />),
              },
              {
                title: 'Print All',
                onClick: () => asyncPrint((props) => (
                  <>
                    <ShipmentPlacard shipment={obj.data} />
                    <PackingSlip {...props} guid={obj.data.guid || ''} />
                  </>
                ))
              },
            ],
          },
          {
            Icon: LabelIcon,
            actions: [
              {
                title: 'Print Labels',
                onClick: () => {
                  window.dispatchEvent(
                    new CustomEvent('skuitCommand', {
                      detail: { command: 'scan', data: { scanned: obj.data } },
                    })
                  )
                  window.dispatchEvent(
                    new CustomEvent('skuitCommand', {
                      detail: { command: 'print' },
                    })
                  )
                },
              },
              {
                title: 'Set SSCC Codes',
                onClick: () => {
                  window.dispatchEvent(
                    new CustomEvent('skuitCommand', {
                      detail: { command: 'scan', data: { scanned: obj.data } },
                    })
                  )
                  window.dispatchEvent(
                    new CustomEvent('skuitCommand', {
                      detail: { command: 'setSSCC' },
                    })
                  )
                },
              },
            ],
          },
        ]}
        fields={[
          { id: 'guid', label: 'SquID', renderer: ValueField },
          { id: 'trackingNumber', label: 'Tracking Number', renderer: ValueField },
          { id: 'bol', label: 'Bill of Lading Number', renderer: ValueField },
          { id: 'shipmentId', label: 'Shipment ID', renderer: ValueField },
          { id: 'proNumber', label: 'Pro#', renderer: ValueField },
          { id: 'client', label: 'Client', renderer: RelatedLinkedField(ValueField, 'name') },
          { id: 'order', label: 'Order', renderer: RelatedLinkedField(ValueField, 'name') },
          {
            id: 'facility',
            label: 'Facility',
            renderer: RelatedLinkedField(ValueField, 'name'),
          },
          {
            id: 'objPath',
            label: 'Path',
            renderer: LinkedPathField,
          },
          {
            id: 'truckload',
            label: 'Truckload',
            renderer: RelatedLinkedField(ValueField, 'name'),
          },
          { id: 'carrier', label: 'Carrier', renderer: RelatedLinkedField(ValueField, 'name') },
          { id: 'address', label: 'Address', renderer: AddressField },
          { id: 'status', label: 'Status', renderer: StatusField },
          { id: 'internalNotes', label: 'Internal Notes', renderer: ValueField },
          { id: 'shippedOn', label: 'Shipped On', ordering: 'shipped_on', renderer: DateTimeField },
        ]}
      />
    </>
  )
}
